import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule, NgForm } from '@angular/forms';
import { FirestoreService } from '../../../services/firestore/firestore.service';
import { SharedService } from '../../../services/shared/shared.service';
import { HeroComponent } from '../../shared/hero/hero.component';
import { CommonModule } from '@angular/common';
import { Timestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { Article } from '../../../models/article';
import { AuthServiceService } from '../../../services/authService/auth-service.service';
import { UserDto } from '../../../models/user-dto';
import { NotificationService } from '../../../services/notification/notification.service';
import { NotificationRequest } from '../../../models/notification-request';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

@Component({
  selector: 'app-article-details',
  imports: [HeroComponent, CommonModule, FormsModule, RouterLink],
  templateUrl: './article-details.component.html',
  styleUrls: ['./article-details.component.css'],
  standalone: true
})
export class ArticleDetailsComponent implements OnInit, OnDestroy {
  article: Article | null = null;
  comments: any[] = [];
  categories: { name: string, count: number }[] = [];
  recentArticles: Article[] = [];
  newComment = { comment: '' };
  newReply = { name: '', email: '', reply: '' };
  showReplyForm: { [key: string]: boolean } = {};
  isAdmin = false;
  showSpinner = true;
  isLoggedIn = false;
  userInfo: UserDto = {
    id: '',
    email: '',
    fullName: '',
  };
  userUid: any;
  adminIds: any[] = [];
  searchQuery: string = '';
  searchResults: Article[] = [];
  showSearchDropdown: boolean = false;
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private firestoreService: FirestoreService,
    private sharedService: SharedService,
    private authService: AuthServiceService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    // Einrichtung der Suchverzögerung
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(query => {
        if (query.trim()) {
          this.performSearch(query);
        } else {
          this.searchResults = [];
          this.showSearchDropdown = false;
        }
      });

    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.firestoreService.getById('articles', id).subscribe({
          next: (data) => {
            this.article = data;
            if (this.article && !sessionStorage.getItem(`viewed_${id}`)) {
              this.updateViews(id);
              sessionStorage.setItem(`viewed_${id}`, 'true');
            }
            this.showSpinner = false;
          },
          error: (err) => {
            this.sharedService.handleError(err, undefined, "Fehler beim Abrufen des Artikels.");
            this.showSpinner = false;
          }
        });
        this.fetchComments(id);
      }
    });
    this.firestoreService.getAdminUserIds().subscribe({
      next: res => this.adminIds = res
    });
    this.fetchSidebarData();
    this.checkAdmin();
    this.isUserLoggedIn();
    this.getUserInfo();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Behandelt Klicks außerhalb des Dropdowns, um es zu schließen
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.search-widget')) {
      this.showSearchDropdown = false;
      this.searchResults = [];
      this.searchQuery = '';
    }
  }

  checkAdmin() {
    this.authService.isAdmin().subscribe({
      next: (res) => {
        this.isAdmin = res;
      },
    });
  }

  updateViews(id: string) {
    if (this.article) {
      const newViews = (this.article.views || 0) + 1;
      this.firestoreService.update('articles', id, { views: newViews }).subscribe({
        error: (err) => {
          console.error('Fehler beim Aktualisieren der Aufrufe:', err);
        }
      });
      this.article.views = newViews;
    }
  }

  fetchComments(id: string) {
    this.firestoreService.getAllSub('articles', id, 'comments').subscribe({
      next: (data) => {
        this.comments = data.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
        this.comments.forEach(comment => {
          this.fetchReplies(id, comment.id);
        });
      },
      error: (err) => {
        console.error('Fehler beim Abrufen der Kommentare:', err);
      }
    });
  }

  fetchReplies(articleId: string, commentId: string) {
    this.firestoreService.getAllSub('articles', articleId, 'comments', commentId, 'replies').subscribe({
      next: (replies) => {
        const index = this.comments.findIndex(c => c.id === commentId);
        if (index !== -1) {
          this.comments[index].replies = replies.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds);
        }
      },
      error: (err) => {
        console.error('Fehler beim Abrufen der Antworten:', err);
      }
    });
  }

  toggleReplyForm(commentId: string) {
    this.showReplyForm[commentId] = !this.showReplyForm[commentId];
    this.newReply = { name: '', email: '', reply: '' };
  }

  fetchSidebarData() {
    this.firestoreService.getAll('articles').subscribe({
      next: (articles: Article[]) => {
        const catMap = new Map<string, number>();
        articles.forEach(a => {
          if (a.category) {
            catMap.set(a.category, (catMap.get(a.category) || 0) + 1);
          }
        });
        this.categories = Array.from(catMap, ([name, count]) => ({ name, count }));

        this.recentArticles = articles
          .filter(a => a.status === 'published' && a.publishedAt)
          .sort((a, b) => {
            const aTime = a.publishedAt?.toDate().getTime() || 0;
            const bTime = b.publishedAt?.toDate().getTime() || 0;
            return bTime - aTime;
          })
          .slice(0, 5);
      },
      error: (err) => {
        console.error('Fehler beim Abrufen der Sidebar-Daten:', err);
      }
    });
  }

  addComment(form: NgForm) {
    if (form.invalid || !this.article) return;

    if (!this.isLoggedIn) {
      Swal.fire({
        title: 'Anmeldung erforderlich',
        text: 'Sie müssen sich anmelden, um einen Kommentar hinzuzufügen.',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    const comment = {
      userId: this.userInfo.id,
      name: this.userInfo.fullName,
      image: this.userInfo.image || '',
      comment: this.newComment.comment,
      createdAt: Timestamp.now()
    };
    const commentText = this.newComment.comment;

    this.firestoreService.addSub('articles', this.article.id, 'comments', comment).subscribe({
      next: () => {
        this.sharedService.handleSuccess('Kommentar erfolgreich hinzugefügt');
        this.fetchComments(this.article!.id);
        this.newComment = { comment: '' };
        form.resetForm();

        if (this.adminIds && this.adminIds.length > 0 && this.article) {
          const request = new NotificationRequest();
          request.title = "Neuer Kommentar erhalten";
          request.body = `${this.userInfo?.fullName} hat zu "${this.article.title}" kommentiert: ${commentText.substring(0, 50)}...`;
          request.type = "COMMENT";
          request.userIds = this.adminIds;
          request.status = "UNREAD";
          this.notificationService.saveNotification(request).subscribe({
            next: res => {
              console.log('Benachrichtigung gesendet:', res);
            },
            error: err => {
              this.sharedService.handleError(err, undefined, "Fehler beim Senden der Benachrichtigung.");
            }
          });
        }
      },
      error: (err) => {
        this.sharedService.handleError(err, undefined, "Fehler beim Hinzufügen des Kommentars.");
      }
    });
  }

  addReply(commentId: string, form: NgForm) {
    if (form.invalid || !this.article) return;

    if (!this.isLoggedIn) {
      Swal.fire({
        title: 'Anmeldung erforderlich',
        text: 'Sie müssen sich anmelden, um eine Antwort hinzuzufügen.',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    const reply = {
      userId: this.userInfo.id,
      name: this.userInfo.fullName,
      image: this.userInfo.image || '',
      reply: this.newReply.reply,
      createdAt: Timestamp.now()
    };
    const replyText = this.newReply.reply;

    this.firestoreService.addSub('articles', this.article.id, 'comments', commentId, 'replies', reply).subscribe({
      next: () => {
        this.sharedService.handleSuccess('Antwort erfolgreich hinzugefügt');
        this.fetchReplies(this.article!.id, commentId);
        this.toggleReplyForm(commentId);
        form.resetForm();

        if (this.adminIds && this.adminIds.length > 0 && this.article) {
          const request = new NotificationRequest();
          request.title = "Neue Antwort erhalten";
          request.body = `${this.userInfo?.fullName} hat zu "${this.article.title}" geantwortet: ${replyText.substring(0, 50)}...`;
          request.type = "REPLY";
          request.userIds = this.adminIds;
          request.status = "UNREAD";
          this.notificationService.saveNotification(request).subscribe({
            next: res => {
              console.log('Benachrichtigung gesendet:', res);
            },
            error: err => {
              this.sharedService.handleError(err, undefined, "Fehler beim Senden der Benachrichtigung.");
            }
          });
        }
      },
      error: (err) => {
        this.sharedService.handleError(err, undefined, "Fehler beim Hinzufügen der Antwort.");
      }
    });
  }

  deleteComment(articleId: string, commentId: string) {
    if (this.article) {
      Swal.fire({
        title: 'Sind Sie sicher?',
        text: 'Diese Aktion löscht den Kommentar dauerhaft.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ja, löschen!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.firestoreService.delete(`articles/${articleId}/comments`, commentId).subscribe({
            next: () => {
              Swal.fire('Gelöscht!', 'Der Kommentar wurde gelöscht.', 'success');
              this.fetchComments(articleId);
            },
            error: (err) => {
              this.sharedService.handleError(err, undefined, "Fehler beim Löschen des Kommentars.");
            }
          });
        }
      });
    }
  }

  deleteReply(articleId: string, commentId: string, replyId: string) {
    if (this.article) {
      Swal.fire({
        title: 'Sind Sie sicher?',
        text: 'Diese Aktion löscht die Antwort dauerhaft.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Ja, löschen!'
      }).then((result) => {
        if (result.isConfirmed) {
          this.firestoreService.delete(`articles/${articleId}/comments/${commentId}/replies`, replyId).subscribe({
            next: () => {
              Swal.fire('Gelöscht!', 'Die Antwort wurde gelöscht.', 'success');
              this.fetchReplies(articleId, commentId);
            },
            error: (err) => {
              this.sharedService.handleError(err, undefined, "Fehler beim Löschen der Antwort.");
            }
          });
        }
      });
    }
  }

  isUserLoggedIn() {
    this.authService.isUserLoggedIn().subscribe({
      next: res => {
        this.isLoggedIn = res;
        this.userUid = this.authService.getCurrentUserUid();
        this.getUserInfo();
      },
    });
  }

  getUserInfo() {
    if (this.userUid) {
      this.firestoreService.getById("users", this.userUid).subscribe({
        next: res => this.userInfo = res,
        error: err => {
          this.sharedService.handleError(err);
        }
      });
    }
  }

  onSearchInput(query: string) {
    this.searchSubject.next(query);
  }

  performSearch(query: string) {
    this.showSpinner = true;
    this.firestoreService.getAll('articles').subscribe({
      next: (articles: Article[]) => {
        this.searchResults = articles
          .filter(a => 
            a.status === 'published' && 
            a.title?.toLowerCase().includes(query.toLowerCase())
          )
          .sort((a, b) => {
            const aTime = a.publishedAt?.toDate().getTime() || 0;
            const bTime = b.publishedAt?.toDate().getTime() || 0;
            return bTime - aTime;
          })
          .slice(0, 5); // Begrenze auf 5 Ergebnisse für das Dropdown
        this.showSearchDropdown = this.searchResults.length > 0;
        this.showSpinner = false;
        if (this.searchResults.length === 0 && query.trim()) {
          Swal.fire({
            title: 'Keine Ergebnisse',
            text: 'Keine Artikel gefunden, die Ihrer Suchanfrage entsprechen.',
            icon: 'info',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK'
          });
        }
      },
      error: (err) => {
        this.sharedService.handleError(err, undefined, "Fehler beim Suchen nach Artikeln.");
        this.showSpinner = false;
        this.showSearchDropdown = false;
      }
    });
  }

  selectSearchResult(articleId: string) {
    this.searchQuery = '';
    this.searchResults = [];
    this.showSearchDropdown = false;
    this.router.navigate(['/Article', articleId]);
  }
}